import { LoadingOverlay, notify } from "@dehub/theme"
import React, { useEffect, useRef } from "react"
import { mutate as globalMutate } from "swr"
import { useDispatch, useSelector } from "react-redux"
import { Navigate, Route, Routes } from "react-router-dom"
import { AuthApi, GeneralApi } from "./apis"
import { Layout } from "./components/layout"
import { HelpRoot } from "./help/HelpRoot"
import { IPermissionCode, usePermission } from "./components/permission"
import { SupplierDeliveryDistributingForm } from "./components/supplier-delivery-distributing/form"
import { Access } from "./pages/access"
import { ActiveDeliveriesPage } from "./pages/active-deliveries"
import { BuyerCustomerDetail } from "./pages/buyer-customer/detail"
import { BuyerCustomerList } from "./pages/buyer-customer/list"
import { BuyerCustomerOrder } from "./pages/buyer-customer/order"
import { ReceivedQuotationDetail } from "./pages/received-quotation/detail"
import { ReceivedQuotationList } from "./pages/received-quotation/list"
import { BuyerSalesCheckout } from "./pages/buyer-sales/checkout"
import { BuyerSalesDetail } from "./pages/buyer-sales/detail"
import { BuyerSalesEdit } from "./pages/buyer-sales/edit"
import { BuyerSalesList } from "./pages/buyer-sales/list"
import { BuyerSalesNew } from "./pages/buyer-sales/new"
import { BuyerSalesProductDetail } from "./pages/buyer-sales/product-detail"
import { BuyerSalesQuickOrder } from "./pages/buyer-sales/quick-order"
import { BuyerSalesSavedLists } from "./pages/buyer-sales/saved-lists"
import { BuyerSalesPromotions } from "./pages/buyer-sales/promotions"
import { BuyerSalesSuccess } from "./pages/buyer-sales/success"
import { CustodyTransfersPage } from "./pages/custody-transfers"
import { CustodyTransferDetailPage } from "./pages/custody-transfers/detail"
import { DamagedInventoryPage } from "./pages/damaged-inventory"
import { DamagedInventoryDetailPage } from "./pages/damaged-inventory/detail"
import { Dashboard } from "./pages/dashboard"
import { DeliveryRoutesPage } from "./pages/delivery-routes"
import { DeliveryRouteDetailPage } from "./pages/delivery-routes/detail"
import { DeliveryRouteNewPage } from "./pages/delivery-routes/new"
import { SupplierDropShippingTabs } from "./pages/drop-shipping"
import { PendingDeliveriesPage } from "./pages/pending-deliveries"
import { SupplierPurchaseTabs } from "./pages/purchase-sales"
import { PurchaseSalesDetail } from "./pages/purchase-sales/detail"
import { ReturnsSettingsPage } from "./pages/return-settings"
import { FailedOperationsPage } from "./pages/return-settings/failed-operations"
import { NotificationTemplatesPage } from "./pages/return-settings/notification-templates"
import { ProposeTemplatesPage } from "./pages/return-settings/propose-templates"
import { DamagedTurnoverPage } from "./pages/return/damaged-turnover"
import { ReturnDashboardPage } from "./pages/return/dashboard"
import { ReturnDetailPage } from "./pages/return/detail"
import { DisputesListPage } from "./pages/return/disputes"
import { ReturnListPage } from "./pages/return/list"
import { TransitExceptionsPage } from "./pages/return/transit-exceptions"
import { PreventionListPage } from "./pages/return/prevention"
import { PreventionDetailPage } from "./pages/return/prevention/detail"
import { TrustScoresListPage } from "./pages/return/trust-scores"
import { TrustScoreDetailPage } from "./pages/return/trust-scores/detail"
import { WarehouseReceiptPage } from "./pages/return/warehouse-receipt"
import { OffSystemVerifyPage } from "./pages/return/off-system-verify"
import { SupplierCustomerDetail } from "./pages/supplier-customer/detail"
import { SupplierCustomerList } from "./pages/supplier-customer/list"
import { SupplierCustomerOrder } from "./pages/supplier-customer/order"
import { SupplierDeliveryTabs } from "./pages/supplier-delivery"
import { SupplierSalesTabs } from "./pages/supplier-sales"
import { SupplierSalesDetail } from "./pages/supplier-sales/detail"
import { SupplierSalesEdit } from "./pages/supplier-sales/edit"
import { SupplierSalesNew } from "./pages/supplier-sales/new"
import { SupplierShippingTab } from "./pages/supplier-shipping"
import { authMe, IAuth } from "./store/Auth"
import { initGeneral } from "./store/General"
import { message } from "./utils/message"

import { SupplierDeliveryDistributingDetail } from "./components/supplier-delivery-distributing/detail"
import { SupplierDropShippingDetail } from "./components/supplier-dropshipping/detail"
import { SupplierShippingCustomerDetail } from "./components/supplier-shipping-customer/detail"
import { onMessageListener, requestForToken } from "./firebase/fcmService"
import { InboundPage } from "./pages/inbound"
import { SettingsPage } from "./pages/settings"
import { VehiclePage } from "./pages/vehicle"

// mvp-returns Phase 2 — buyer returns surface (lazy-loaded per spec).
const BuyerReturnsHubPage = React.lazy(() =>
  import("./pages/buyer-app/returns/hub").then((m) => ({ default: m.BuyerReturnsHubPage })),
)
const BuyerReturnWizardPage = React.lazy(() =>
  import("./pages/buyer-app/returns/wizard").then((m) => ({ default: m.BuyerReturnWizardPage })),
)
const BuyerReturnDetailPage = React.lazy(() =>
  import("./pages/buyer-app/returns/detail").then((m) => ({ default: m.BuyerReturnDetailPage })),
)
const BuyerAppOrderDetailPage = React.lazy(() =>
  import("./pages/buyer-app/order-detail").then((m) => ({ default: m.BuyerAppOrderDetailPage })),
)
// mvp-returns Phase 5 — buyer notifications inbox (screens-buyer-app §22).
const BuyerNotificationsPage = React.lazy(() =>
  import("./pages/buyer-app/notifications").then((m) => ({ default: m.BuyerNotificationsPage })),
)

// Renders children only when the user holds `permission`; otherwise shows the
// standard access-denied alert. No permission → render through.
function PermissionGate({ permission, children }: { permission?: IPermissionCode; children: React.ReactNode }) {
  const perm = usePermission(permission ?? "ORD_LIST")
  if (permission && !perm.hasAccess) return perm.accessDenied()
  return <>{children}</>
}

// Wraps a lazy buyer-returns page in Suspense + a permission gate. RETURN_VIEW
// guards the view surface; RETURN_CREATE guards the create wizard (D19).
function BuyerReturnsRoute({ element, permission }: { element: React.ReactNode; permission?: IPermissionCode }) {
  return (
    <React.Suspense fallback={<LoadingOverlay visible />}>
      <PermissionGate permission={permission}>{element}</PermissionGate>
    </React.Suspense>
  )
}

function HomeRedirect() {
  const { user } = useSelector((state: { auth: IAuth }) => state.auth)
  const isBuyer = user?.currentBusiness?.type !== "SUPPLIER"

  if (isBuyer) return <Navigate to="/buyer-sales/new" replace />
  return <Dashboard />
}

let __WINDOW_LOADED__ = false

function App() {
  const [loading, setLoading] = React.useState(false)
  const dispatch = useDispatch()
  const hasRequestedToken = useRef<string | null>(null)

  const { accessToken } = useSelector((state: { auth: IAuth }) => state.auth)

  // Register the browser's FCM token for every new session. Running it once on
  // mount fired before /access stored the token (401, never retried), and logout
  // clears the user's web tokens, so a fresh login never got pushes again.
  useEffect(() => {
    if (!accessToken || hasRequestedToken.current === accessToken) return
    hasRequestedToken.current = accessToken
    requestForToken()
  }, [accessToken])

  useEffect(() => {
    onMessageListener((payload: any) => {
      notify.info({
        id: `notif-${Math.random()}`,
        title: payload?.notification?.title || "New Notification",
        message: payload?.notification?.body || "You have received a new notification.",
        autoClose: 6000,
      })
      // Show the new row in the header bell without a reload.
      globalMutate("/2fa/auth/fcm/list")
    })
  }, [])

  const init = React.useCallback(async () => {
    setLoading(true)
      ; (async () => {
        try {
          if (accessToken) {
            const res = await GeneralApi.init()
            dispatch(initGeneral(res))

            const auth = await AuthApi.me()
            dispatch(authMe(auth))
          }
          setLoading(false)
        } catch (error: any) {
          message.error(error?.message)
          setLoading(false)
        }
      })()
    setLoading(false)
  }, [accessToken, dispatch])

  React.useEffect(() => {
    if (!__WINDOW_LOADED__) init()

    __WINDOW_LOADED__ = true
  }, [init])

  if (loading) return <LoadingOverlay visible={loading} />

  return (
    <Routes>
      {/* /access bootstraps auth from the URL token — must render outside
          Layout so Layout's Header doesn't fire protected API calls with
          the stale persisted token and bounce us back to auth-admin on 401. */}
      <Route path="/access" element={<Access />} />
      <Route path="/" element={<HelpRoot><Layout /></HelpRoot>}>
        <Route path="/" element={<HomeRedirect />} />
        {/* Canonical dashboard route. "/" still dispatches by portal — a buyer
            landing there is sent to the catalogue — so the dashboard needs an
            address of its own, otherwise the buyer's own home page is
            unreachable from anywhere in the app. */}
        <Route path="/dashboard" element={<Dashboard />} />
        {/* BUYER */}
        <Route path="/buyer-sales" element={<BuyerSalesList />} />
        <Route path="/buyer-sales/new" element={<BuyerSalesNew />} />
        <Route path="/buyer-sales/quick-order" element={<BuyerSalesQuickOrder />} />
        <Route path="/buyer-sales/saved-lists" element={<BuyerSalesSavedLists />} />
        <Route path="/buyer-sales/promotions" element={<BuyerSalesPromotions />} />
        <Route path="/buyer-sales/product/:id" element={<BuyerSalesProductDetail />} />
        <Route path="/buyer-sales/checkout" element={<BuyerSalesCheckout />} />
        <Route path="/buyer-sales/success" element={<BuyerSalesSuccess />} />
        <Route path="/buyer-sales/detail/:id" element={<BuyerSalesDetail />} />
        <Route path="/buyer-sales/edit/:id" element={<BuyerSalesEdit />} />

        <Route path="/buyer-customer" element={<BuyerCustomerList />} />
        <Route path="/buyer-customer/order/:id" element={<BuyerCustomerOrder />} />
        <Route path="/buyer-customer/detail/:id" element={<BuyerCustomerDetail />} />

        <Route path="/received-quotation" element={<ReceivedQuotationList />} />
        <Route path="/received-quotation/:id" element={<ReceivedQuotationDetail />} />

        {/* SUPPLIER */}
        <Route path="/supplier-sales" element={<SupplierSalesTabs />} />
        <Route path="/supplier-sales/new" element={<SupplierSalesNew />} />
        <Route path="/supplier-sales/edit/:id/:receiverBusinessId" element={<SupplierSalesEdit />} />
        <Route path="/supplier-sales/detail/:id" element={<SupplierSalesDetail />} />

        <Route path="/supplier-purchase" element={<SupplierPurchaseTabs />} />
        <Route path="/supplier-purchase/detail/:id" element={<PurchaseSalesDetail />} />

        <Route path="/supplier-drop-shipping" element={<SupplierDropShippingTabs />} />
        <Route path="/supplier-drop-shipping/detail/:id" element={<SupplierDropShippingDetail />} />
        <Route path="/supplier-drop-shipping/:type/detail/:id" element={<SupplierDropShippingDetail />} />

        <Route path="/supplier-delivery" element={<SupplierDeliveryTabs />} />
        <Route path="/supplier-delivery/approve" element={<SupplierDeliveryDistributingForm />} />
        <Route path="/supplier-delivery/detail/:id" element={<SupplierDeliveryDistributingDetail />} />

        <Route path="/supplier-shipping" element={<SupplierShippingTab />} />
        <Route path="/supplier-shipping/detail/:id" element={<SupplierShippingCustomerDetail />} />

        <Route path="/pending-deliveries" element={<PendingDeliveriesPage />} />
        <Route path="/delivery-routes" element={<DeliveryRoutesPage />} />
        <Route path="/delivery-routes/new" element={<DeliveryRouteNewPage />} />
        <Route path="/delivery-routes/:id" element={<DeliveryRouteDetailPage />} />

        {/* D6 — Active deliveries */}
        <Route path="/active-deliveries" element={<ActiveDeliveriesPage />} />

        <Route path="/supplier-customer" element={<SupplierCustomerList />} />
        <Route path="/supplier-customer/detail/:id" element={<SupplierCustomerDetail />} />
        <Route path="/supplier-customer/order/:id" element={<SupplierCustomerOrder />} />

        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/vehicle" element={<VehiclePage />} />

        {/* HANDOVER */}
        <Route path="/handover" element={<CustodyTransfersPage />} />
        <Route path="/handover/:id" element={<CustodyTransferDetailPage />} />

        {/* RETURNS — mvp-returns Phase E. §9.1 list is the /returns landing;
            the KPI dashboard lives at /returns/dashboard. */}
        <Route path="/returns" element={<ReturnListPage />} />
        <Route path="/returns/list" element={<ReturnListPage />} />
        <Route path="/returns/dashboard" element={<ReturnDashboardPage />} />
        <Route path="/returns/disputes" element={<DisputesListPage />} />
        <Route path="/returns/transit-exceptions" element={<TransitExceptionsPage />} />
        <Route path="/returns/trust-scores" element={<TrustScoresListPage />} />
        <Route path="/returns/trust-scores/:businessId" element={<TrustScoreDetailPage />} />
        <Route path="/returns/prevention" element={<PreventionListPage />} />
        <Route path="/returns/prevention/:id" element={<PreventionDetailPage />} />
        <Route path="/returns/:id/warehouse-receipt" element={<WarehouseReceiptPage />} />
        <Route path="/returns/:id/off-system-verify" element={<OffSystemVerifyPage />} />
        <Route path="/returns/:id" element={<ReturnDetailPage />} />
        <Route path="/damaged-inventory" element={<PermissionGate permission="DAMAGED_INVENTORY_VIEW"><DamagedInventoryPage /></PermissionGate>} />
        <Route path="/damaged-inventory/:id" element={<PermissionGate permission="DAMAGED_INVENTORY_VIEW"><DamagedInventoryDetailPage /></PermissionGate>} />
        <Route path="/damaged-inventory/turnover" element={<PermissionGate permission="DAMAGED_INVENTORY_VIEW"><DamagedTurnoverPage /></PermissionGate>} />
        <Route path="/returns/settings" element={<ReturnsSettingsPage />} />
        <Route path="/returns/settings/notification-templates" element={<NotificationTemplatesPage />} />
        <Route path="/returns/settings/propose-templates" element={<ProposeTemplatesPage />} />
        <Route path="/returns/settings/failed-operations" element={<FailedOperationsPage />} />

        {/* Legacy redirects — entries moved into supplier-delivery / supplier-shipping tabs */}
        <Route path="/failed-deliveries" element={<Navigate to="/supplier-delivery?tab=FAILED" replace />} />
        <Route path="/picking" element={<Navigate to="/supplier-shipping?tab=SHORTAGE" replace />} />

        {/* INBOUND */}
        <Route path="/inbound" element={<InboundPage />} />

        {/* BUYER RETURNS — mvp-returns Phase 2 (screens-buyer-app, §8 routing).
            Hub + detail gate on RETURN_VIEW; the create wizard on RETURN_CREATE.
            Order numbering: /new is matched before /:returnId. */}
        <Route
          path="/buyer-app/my-returns"
          element={<BuyerReturnsRoute permission="RETURN_VIEW" element={<BuyerReturnsHubPage />} />}
        />
        <Route
          path="/buyer-app/my-returns/new"
          element={<BuyerReturnsRoute permission="RETURN_CREATE" element={<BuyerReturnWizardPage />} />}
        />
        <Route
          path="/buyer-app/my-returns/:returnId"
          element={<BuyerReturnsRoute permission="RETURN_VIEW" element={<BuyerReturnDetailPage />} />}
        />
        <Route
          path="/buyer-app/orders/:orderId"
          element={<BuyerReturnsRoute permission="RETURN_VIEW" element={<BuyerAppOrderDetailPage />} />}
        />
        <Route
          path="/buyer-app/orders/:orderId/return"
          element={<BuyerReturnsRoute permission="RETURN_CREATE" element={<BuyerReturnWizardPage />} />}
        />
        {/* §22 notifications inbox — ungated (mirrors the universal header bell;
            surfaces returns + order updates). Returns rows deep-link to detail. */}
        <Route
          path="/buyer-app/notifications"
          element={<BuyerReturnsRoute element={<BuyerNotificationsPage />} />}
        />
      </Route>
    </Routes>
  )
}

export default App
